// src/components/common/Navbar.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import NotificationBellWithDropdown from './NotificationBellWithDropdown';
import { FaBars, FaSearch, FaUser, FaSignOutAlt, FaCog } from 'react-icons/fa';

const Navbar = ({ toggleSidebar }) => {
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const dropdownMenuRef = useRef(null);
  
  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Dynamic positioning to prevent cutoff
  useEffect(() => {
    if (profileDropdownOpen && dropdownMenuRef.current) {
      // Small delay to ensure DOM has rendered
      const positionDropdown = () => {
        const dropdown = dropdownMenuRef.current;
        if (!dropdown) return;
        
        const rect = dropdown.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        // Reset all positioning first
        dropdown.style.transform = 'translateX(0)';
        dropdown.style.right = '0';
        dropdown.style.left = 'auto';
        dropdown.style.top = 'calc(100% + 8px)';
        dropdown.style.bottom = 'auto';
        
        // Small delay to let the browser recalculate
        requestAnimationFrame(() => {
          // Get updated rect after reset
          const updatedRect = dropdown.getBoundingClientRect();
          
          // Handle vertical positioning first
          const spaceBelow = viewportHeight - updatedRect.top;
          const dropdownHeight = updatedRect.height;
          
          if (dropdownHeight > spaceBelow - 20) {
            // Not enough space below, position above
            dropdown.style.top = 'auto';
            dropdown.style.bottom = 'calc(100% + 8px)';
          }
          
          // Handle horizontal positioning
          const finalRect = dropdown.getBoundingClientRect();
          if (finalRect.right > viewportWidth - 10) {
            // Adjust positioning to prevent cutoff
            const overflow = finalRect.right - viewportWidth + 20;
            dropdown.style.transform = `translateX(-${overflow}px)`;
          }
          
          // For very small screens, use different positioning
          if (viewportWidth < 400) {
            dropdown.style.right = '10px';
            dropdown.style.left = '10px';
            dropdown.style.transform = 'translateX(0)';
            dropdown.style.width = 'auto';
            
            // Still handle vertical positioning on small screens
            if (dropdownHeight > spaceBelow - 20) {
              dropdown.style.top = 'auto';
              dropdown.style.bottom = 'calc(100% + 8px)';
            }
          }
        });
      };
      
      // Run positioning logic
      positionDropdown();
    }
  }, [profileDropdownOpen]);
  
  // Toggle profile dropdown
  const toggleProfileDropdown = () => {
    setProfileDropdownOpen(!profileDropdownOpen);
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <div className="navbar-left">
          {currentUser && (
            <button className="menu-toggle" onClick={toggleSidebar}>
              <FaBars />
            </button>
          )}
          
          {!currentUser && (
            <Link to="/" className="navbar-brand">
              <span className="brand-logo">T</span>
              <span className="brand-text">TotsPal</span>
            </Link>
          )}
        </div>
        
        {/* <div className="navbar-search">
          <div className="search-container">
            <FaSearch className="search-icon" />
            <input 
              type="search" 
              placeholder="Search for childcare, activities..." 
              className="search-input" 
            />
          </div>
        </div> */}
        
        <div className="navbar-right">
          {currentUser ? (
            <>
              <div className="navbar-actions">
                <NotificationBellWithDropdown />
              </div>
              
              <div className="profile-dropdown" ref={dropdownRef}>
                <button 
                  className="profile-button"
                  onClick={toggleProfileDropdown}
                >
                  {userProfile?.profile?.photoURL ? (
                    <img 
                      src={userProfile.profile.photoURL} 
                      alt="Profile" 
                      className="avatar"
                    />
                  ) : currentUser?.photoURL ? (
                    <img 
                      src={currentUser.photoURL} 
                      alt="Profile" 
                      className="avatar"
                    />
                  ) : (
                    <div className="avatar">
                      {(userProfile?.profile?.displayName || userProfile?.displayName || currentUser?.displayName || 'U').charAt(0).toUpperCase()}
                    </div>
                  )}
                </button>
                
                {profileDropdownOpen && (
                  <div className="dropdown-menu" ref={dropdownMenuRef}>
                    <div className="dropdown-header">
                      <div className="dropdown-user-info">
                        <strong>{userProfile?.displayName || 'User'}</strong>
                        <span className="text-muted">{currentUser.email}</span>
                      </div>
                    </div>
                    
                    <div className="dropdown-divider"></div>
                    
                    <Link to="/account/settings" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                      <FaCog className="dropdown-icon" />
                      Settings
                    </Link>
                    
                    <div className="dropdown-divider"></div>
                    
                    <button 
                      className="dropdown-item text-danger"
                      onClick={handleLogout}
                    >
                      <FaSignOutAlt className="dropdown-icon" />
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="auth-buttons">
              <Link to="/auth/login" className="btn btn-outline btn-sm">
                Log In
              </Link>
              <Link to="/auth/register" className="btn btn-primary btn-sm">
                Sign Up
              </Link>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;